import { useState, useMemo } from "react";
import { Box, Button, Container, Skeleton, Stack, Typography as T } from "@mui/material";
import DatasetResult from "catalog/components/DatasetResult";
import VariableResult from "catalog/components/VariableResult";
import { PAGE_SIZE } from "catalog/hooks/useCatalogSearch";
import useFilterParams from "catalog/hooks/useFilterParams";
import usePinnedEntities from "catalog/hooks/usePinnedEntities";
import EmptyState from "~/components/EmptyState";
import FlatCard from "~/components/FlatCard";
import GenerateCodeButton from "~/components/GenerateCodeButton";
import LimitedPagination from "~/components/LimitedPagination";
import ScrollableTabContent from "~/components/ScrollableTabContent";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { TableEntity, VariableEntity } from "~/types/interfaces/catalogResponse";

interface CodeGenSelection {
  schema: string;
  table: string;
  variables?: string[];
}

const entityMapper = (entities: (TableEntity | VariableEntity)[]) =>
  entities
    .map((entity): CodeGenSelection | undefined => {
      if (entity.entityType === "table") {
        return {
          schema: (entity as TableEntity).databaseSchema.name,
          table: entity.name,
        };
      }
      if (entity.entityType === "column") {
        const linkedDataset = (entity as VariableEntity).table;
        return {
          schema: linkedDataset.databaseSchema.name,
          table: linkedDataset.name,
          variables: [entity.name],
        };
      }
    })
    .reduce<CodeGenSelection[]>((acc, selection) => {
      if (selection) {
        const existingDatasetIdx = acc.findIndex(
          ({ schema, table }) => selection.schema === schema && selection.table === table
        );

        // Add dataset/variable for first time
        if (existingDatasetIdx < 0) {
          return [...acc, selection];
        }

        // Update existing dataset entry and combine variables
        const existingDataset = acc[existingDatasetIdx];
        if (existingDataset.variables && selection.variables) {
          acc[existingDatasetIdx] = {
            ...existingDataset,
            variables: [...existingDataset.variables, ...selection.variables],
          };
        }
      }
      return acc;
    }, []);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <T variant="h6" fontWeight={600} mb={1}>
    {children}
  </T>
);

const Pins = () => {
  useDocumentTitle("Pins");
  // Note: App header is managed by DataBrowser parent component
  const [{ page }] = useFilterParams();
  const [selectedEntities, setSelectedEntities] = useState<(TableEntity | VariableEntity)[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const { entities, total, loading, removeEntities, isPinned } = usePinnedEntities();
  // Filter by isPinned for optimistic removal - local storage updates immediately
  // while SWR data takes time to refetch
  const allPinnedEntities = Object.values(entities).filter(({ fullyQualifiedName }) =>
    isPinned(fullyQualifiedName)
  );
  const numberPinned = allPinnedEntities.length;
  const showSkeletons = !total && loading;
  const start = page ? (page - 1) * PAGE_SIZE : 0;
  const end = start + PAGE_SIZE;
  const currentPageResults = allPinnedEntities.slice(start, end);
  const pinnedDatasets = currentPageResults.filter(
    ({ entityType }) => entityType === "table"
  ) as TableEntity[];
  const pinnedVariables = currentPageResults.filter(
    ({ entityType }) => entityType === "column"
  ) as VariableEntity[];

  const codeGenSelections = useMemo(() => {
    if (selectMode) {
      return entityMapper(selectedEntities);
    }
    return entityMapper(allPinnedEntities);
  }, [selectMode, selectedEntities, allPinnedEntities]);

  if (!loading && numberPinned === 0) {
    return (
      <ScrollableTabContent bgcolor="common.dimmedPageBackground">
        <Box pt={2} pb={3}>
          <Container maxWidth="lg">
            <FlatCard sx={{ px: 2, py: 4 }}>
              <EmptyState label="Pin tables or columns to quickly access them here" />
            </FlatCard>
          </Container>
        </Box>
      </ScrollableTabContent>
    );
  }

  return (
    <ScrollableTabContent bgcolor="common.dimmedPageBackground">
      <Box pt={2} pb={3}>
        <Container maxWidth="lg">
          {/* Action buttons */}
          <Box display="flex" gap={1} mb={2}>
            {!selectMode && (
              <>
                <Button variant="outlined" onClick={() => setSelectMode(true)}>
                  Select
                </Button>
                <GenerateCodeButton variant="outlined" selections={codeGenSelections} />
              </>
            )}

            {selectMode && (
              <>
                <Button variant="outlined" onClick={() => setSelectMode(false)}>
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedEntities(Object.values(entities ?? {}));
                  }}
                >
                  Select All
                </Button>
                <Button
                  variant="outlined"
                  disabled={selectedEntities.length === 0}
                  onClick={() => {
                    setSelectedEntities([]);
                  }}
                >
                  Select None
                </Button>
                <Button
                  variant="outlined"
                  sx={{ ml: 1 }}
                  disabled={selectedEntities.length === 0}
                  startIcon={
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <g clipPath="url(#clip0_53_2958)">
                        <path
                          d="M6 5.50016L10.6667 10.1668H8.66667V12.8335L8 14.8335L7.33333 12.8335V10.1668H4V8.16683L6 6.16683V5.50016ZM11.3333 0.833496V2.16683L10 2.8335V6.16683L12 8.16683V9.8075L8.18 5.98683L6 3.80683V2.8335L4.66667 2.16683V0.833496H11.3333Z"
                          fill="currentColor"
                        />
                        <path
                          d="M1.51341 1.01318L0.666748 1.85985L13.6401 14.8332L14.4867 13.9865L7.33341 6.83318L1.51341 1.01318Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_53_2958">
                          <rect width="15" height="15" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  }
                  onClick={() => {
                    removeEntities(
                      selectedEntities.map(({ fullyQualifiedName }) => fullyQualifiedName)
                    );
                    setSelectedEntities([]);
                    setSelectMode(false);
                  }}
                >
                  Unpin
                </Button>
                <GenerateCodeButton variant="outlined" selections={codeGenSelections} />
              </>
            )}
          </Box>

          {/* Loading skeletons */}
          {showSkeletons && (
            <Stack gap={2}>
              <Skeleton variant="rounded" height={200} />
              <Skeleton variant="rounded" height={200} />
            </Stack>
          )}

          {/* Columns Card */}
          {pinnedVariables?.length > 0 && (
            <Box mb={3}>
              <SectionTitle>Columns</SectionTitle>
              <FlatCard sx={{ px: 2 }}>
                <Stack gap={1} py={1} pl={5}>
                  {pinnedVariables.map((entity) => (
                    <VariableResult
                      key={entity.fullyQualifiedName}
                      entity={entity}
                      selectable={selectMode}
                      selected={selectedEntities
                        .map(({ fullyQualifiedName }) => fullyQualifiedName)
                        .includes(entity.fullyQualifiedName)}
                      toggleSelected={(checked: boolean) => {
                        if (checked) {
                          setSelectedEntities([...selectedEntities, entity]);
                        } else {
                          setSelectedEntities(
                            selectedEntities.filter(
                              ({ fullyQualifiedName }) =>
                                entity.fullyQualifiedName !== fullyQualifiedName
                            )
                          );
                        }
                      }}
                    />
                  ))}
                </Stack>
              </FlatCard>
            </Box>
          )}

          {/* Tables Card */}
          {pinnedDatasets?.length > 0 && (
            <Box mb={3}>
              <SectionTitle>Tables</SectionTitle>
              <FlatCard sx={{ px: 2 }}>
                <Stack gap={1} py={1} pl={5}>
                  {pinnedDatasets.map((entity) => (
                    <DatasetResult
                      key={entity.fullyQualifiedName}
                      entity={entity}
                      selectable={selectMode}
                      selected={selectedEntities
                        .map(({ fullyQualifiedName }) => fullyQualifiedName)
                        .includes(entity.fullyQualifiedName)}
                      toggleSelected={(checked: boolean) => {
                        if (checked) {
                          setSelectedEntities([...selectedEntities, entity]);
                        } else {
                          setSelectedEntities(
                            selectedEntities.filter(
                              ({ fullyQualifiedName }) =>
                                entity.fullyQualifiedName !== fullyQualifiedName
                            )
                          );
                        }
                      }}
                    />
                  ))}
                </Stack>
              </FlatCard>
            </Box>
          )}

          {/* Pagination */}
          <LimitedPagination total={numberPinned} defaultPageSize={PAGE_SIZE} />
        </Container>
      </Box>
    </ScrollableTabContent>
  );
};

export default Pins;
